import React, { useState, useMemo } from 'react';
import { IndustryName } from '../../../data/industryDefaults';
import { INDUSTRY_DEFAULTS } from '../../../data/industryDefaults';

// Defensible multipliers with sources
const SALARY_MULTIPLIERS = {
  entry: 0.70,      // BLS: Entry typically 60-75% of median
  mid: 1.0,         // Base = median by definition
  senior: 1.45,     // BLS: Senior typically 140-150% of median
  executive: 2.2    // BLS: Exec typically 200-250% of median
};

const VACANCY_MULTIPLIERS = {
  entry: 1.5,       // SHRM: Lower impact roles
  mid: 2.0,         // SHRM: Average multiplier
  senior: 2.5,      // SHRM: Specialized roles
  executive: 3.0    // SHRM: Maximum validated (reduced from 4.0)
};

// Preset profiles for quick selection
const PRESET_PROFILES = {
  early_career: {
    name: 'Early Career Focus',
    entry: 60,
    mid: 30,
    senior: 10,
    executive: 0,
    description: 'Growing teams, entry-level heavy'
  },
  balanced: {
    name: 'Balanced Mix',
    entry: 30,
    mid: 40,
    senior: 25,
    executive: 5,
    description: 'Typical established company'
  },
  experienced: {
    name: 'Experienced Talent',
    entry: 10,
    mid: 40,
    senior: 40,
    executive: 10,
    description: 'Mature organization, senior roles'
  },
  leadership: {
    name: 'Leadership Heavy',
    entry: 5,
    mid: 25,
    senior: 50,
    executive: 20,
    description: 'Transformation or scaling leadership'
  }
};

const LOCATION_ADJUSTMENTS = {
  tech_hub: { label: 'Premium Market (+25%)', multiplier: 1.25 },
  major_metro: { label: 'Major Market (+10%)', multiplier: 1.10 },
  baseline: { label: 'Industry Baseline', multiplier: 1.0 },
  lower_cost: { label: 'Emerging Market (-15%)', multiplier: 0.85 }
};

interface HiringMix {
  entry: number;
  mid: number;
  senior: number;
  executive: number;
}

interface HiringProfileCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (values: {
    salary: number;
    vacancy: number;
    basis: {
      profile?: string;
      location: string;
      customized: boolean;
    };
  }) => void;
  industryName: string;
  currentSalary?: number | string;
  currentVacancy?: number | string;
}

export const HiringProfileCalculator: React.FC<HiringProfileCalculatorProps> = ({
  isOpen,
  onClose,
  onApply,
  industryName,
  currentSalary,
  currentVacancy
}) => {
  // Initialize with explicit values to ensure proper initialization
  const [hiringMix, setHiringMix] = useState<HiringMix>({
    entry: PRESET_PROFILES.balanced.entry,
    mid: PRESET_PROFILES.balanced.mid,
    senior: PRESET_PROFILES.balanced.senior,
    executive: PRESET_PROFILES.balanced.executive
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('balanced');
  const [location, setLocation] = useState('baseline');
  const [manualSalary, setManualSalary] = useState<string>('');
  const [manualVacancy, setManualVacancy] = useState<string>('');
  const [showBreakdown, setShowBreakdown] = useState(false);

  // State for custom salaries - null means use default
  const [customSalaries, setCustomSalaries] = useState<{
    entry: number | null;
    mid: number | null;
    senior: number | null;
    executive: number | null;
  }>({
    entry: null,
    mid: null,
    senior: null,
    executive: null
  });

  // Track if salary section is expanded
  const [showSalaryCustomization, setShowSalaryCustomization] = useState(false);

  // Calculate base salaries for the industry
  const getBaseSalaries = () => {
    const baseSalary = INDUSTRY_DEFAULTS[industryName as IndustryName]?.avgSalary || 90000;
    return {
      entry: Math.round(baseSalary * SALARY_MULTIPLIERS.entry),
      mid: Math.round(baseSalary * SALARY_MULTIPLIERS.mid),
      senior: Math.round(baseSalary * SALARY_MULTIPLIERS.senior),
      executive: Math.round(baseSalary * SALARY_MULTIPLIERS.executive)
    };
  };

  const baseSalaries = useMemo(() => getBaseSalaries(), [industryName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate the total percentage - ensure values are numbers
  const total = useMemo(() => {
    const values = Object.values(hiringMix);
    // Ensure all values are valid numbers
    const sum = values.reduce((acc, val) => {
      const num = Number(val) || 0;
      return acc + num;
    }, 0);
    return Math.round(sum); // Round to avoid floating point issues
  }, [hiringMix]);

  // Calculate values based on current selections
  const calculateValues = () => {
    const locationMultiplier = LOCATION_ADJUSTMENTS[location as keyof typeof LOCATION_ADJUSTMENTS].multiplier;

    // Use custom salaries if set, otherwise use base salaries
    const effectiveSalaries = {
      entry: customSalaries.entry || baseSalaries.entry,
      mid: customSalaries.mid || baseSalaries.mid,
      senior: customSalaries.senior || baseSalaries.senior,
      executive: customSalaries.executive || baseSalaries.executive
    };

    // Calculate weighted average salary
    const weightedSalary =
      (hiringMix.entry / 100) * effectiveSalaries.entry +
      (hiringMix.mid / 100) * effectiveSalaries.mid +
      (hiringMix.senior / 100) * effectiveSalaries.senior +
      (hiringMix.executive / 100) * effectiveSalaries.executive;

    // Apply location adjustment
    const adjustedSalary = Math.round(weightedSalary * locationMultiplier);

    // Calculate weighted vacancy cost multiplier
    const weightedMultiplier =
      (hiringMix.entry / 100) * VACANCY_MULTIPLIERS.entry +
      (hiringMix.mid / 100) * VACANCY_MULTIPLIERS.mid +
      (hiringMix.senior / 100) * VACANCY_MULTIPLIERS.senior +
      (hiringMix.executive / 100) * VACANCY_MULTIPLIERS.executive;

    const dailyVacancy = Math.round((adjustedSalary * weightedMultiplier) / 365);

    return { avgSalary: adjustedSalary, dailyVacancy };
  };

  const { avgSalary, dailyVacancy } = calculateValues();

  // Apply preset profile
  const applyPreset = (presetKey: string) => {
    const preset = PRESET_PROFILES[presetKey as keyof typeof PRESET_PROFILES];
    if (!preset) return; // Guard against invalid preset keys

    setHiringMix({
      entry: Number(preset.entry) || 0,
      mid: Number(preset.mid) || 0,
      senior: Number(preset.senior) || 0,
      executive: Number(preset.executive) || 0
    });
    setSelectedPreset(presetKey);
  };

  // Smart percentage adjustment using largest remainder method
  const handleMixChange = (role: keyof HiringMix, newValue: number) => {

    // Ensure newValue is within bounds
    newValue = Math.max(0, Math.min(100, newValue));

    const otherRoles = (Object.keys(hiringMix) as Array<keyof HiringMix>).filter(k => k !== role);
    const remainingPercentage = 100 - newValue;

    const newMix = { ...hiringMix, [role]: newValue };

    // Handle edge cases
    if (newValue === 100) {
      // Set all others to 0
      otherRoles.forEach(key => {
        newMix[key] = 0;
      });
    } else if (remainingPercentage === 0) {
      // This shouldn't happen due to bounds check, but handle it anyway
      otherRoles.forEach(key => {
        newMix[key] = 0;
      });
    } else {
      // Calculate the sum of other roles (excluding the one being changed)
      const sumOthers = otherRoles.reduce((sum, key) => sum + hiringMix[key], 0);

      if (sumOthers === 0) {
        // If all others are 0, distribute evenly
        const equalShare = Math.floor(remainingPercentage / otherRoles.length);
        const remainder = remainingPercentage - (equalShare * otherRoles.length);

        otherRoles.forEach((key, index) => {
          newMix[key] = equalShare + (index < remainder ? 1 : 0);
        });
      } else {
        // Use largest remainder method for proportional distribution
        const scale = remainingPercentage / sumOthers;

        // Calculate ideal values and their integer parts
        const calculations = otherRoles.map(key => {
          const ideal = hiringMix[key] * scale;
          const floor = Math.floor(ideal);
          return {
            key,
            ideal,
            floor,
            remainder: ideal - floor
          };
        });

        // Sum of all floor values
        const sumFloors = calculations.reduce((sum, calc) => sum + calc.floor, 0);
        const toDistribute = remainingPercentage - sumFloors;

        // Sort by remainder (descending) to apply largest remainder method
        calculations.sort((a, b) => b.remainder - a.remainder);

        // Assign values using largest remainder method
        calculations.forEach((calc, index) => {
          newMix[calc.key] = calc.floor + (index < toDistribute ? 1 : 0);
        });
      }
    }

    // Final validation to ensure exactly 100%
    const finalTotal = Object.values(newMix).reduce((sum, val) => sum + val, 0);
    if (finalTotal !== 100 && finalTotal > 0) {
      // This should rarely happen due to our algorithm, but handle it as a safety net
      const diff = 100 - finalTotal;
      const largestOther = otherRoles.reduce((max, key) =>
        newMix[key] > newMix[max] ? key : max, otherRoles[0]);
      if (largestOther) {
        newMix[largestOther] = Math.max(0, newMix[largestOther] + diff);
      }
    }

    setHiringMix(newMix);
    setSelectedPreset(''); // Clear preset selection when manually adjusting
  };

  const handleApply = () => {
    const finalSalary = manualSalary ? parseInt(manualSalary.replace(/,/g, '')) : avgSalary;
    const finalVacancy = manualVacancy ? parseInt(manualVacancy.replace(/,/g, '')) : dailyVacancy;

    onApply({
      salary: finalSalary,
      vacancy: finalVacancy,
      basis: {
        profile: selectedPreset || 'custom',
        location,
        customized: !!(manualSalary || manualVacancy || customSalaries.entry || customSalaries.mid || customSalaries.senior || customSalaries.executive)
      }
    });
  };

  // Format number with commas
  const formatWithCommas = (value: number | string): string => {
    const num = typeof value === 'string' ? value.replace(/,/g, '') : String(value);
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Handle salary input changes
  const handleSalaryChange = (tier: keyof typeof customSalaries, value: string) => {
    const cleanValue = value.replace(/[^0-9,]/g, '');
    const numValue = parseInt(cleanValue.replace(/,/g, ''), 10);

    if (isNaN(numValue) || numValue === 0) {
      setCustomSalaries(prev => ({ ...prev, [tier]: null }));
    } else {
      setCustomSalaries(prev => ({ ...prev, [tier]: numValue }));
    }
  };

  // Reset salary to default
  const resetSalary = (tier: keyof typeof customSalaries) => {
    setCustomSalaries(prev => ({ ...prev, [tier]: null }));
  };

  // Check if salary is customized
  const isCustomized = (tier: keyof typeof customSalaries): boolean => {
    return customSalaries[tier] !== null;
  };

  // Validate salary progression
  const getSalaryWarnings = (): string[] => {
    const warnings: string[] = [];
    const salaries = {
      entry: customSalaries.entry || baseSalaries.entry,
      mid: customSalaries.mid || baseSalaries.mid,
      senior: customSalaries.senior || baseSalaries.senior,
      executive: customSalaries.executive || baseSalaries.executive
    };

    if (salaries.entry >= salaries.mid) {
      warnings.push('Entry level salary is higher than or equal to mid-level');
    }
    if (salaries.mid >= salaries.senior) {
      warnings.push('Mid-level salary is higher than or equal to senior level');
    }
    if (salaries.senior >= salaries.executive) {
      warnings.push('Senior level salary is higher than or equal to executive level');
    }

    // Check for extreme deviations from industry norms (>50% difference)
    Object.entries(salaries).forEach(([tier, salary]) => {
      const baseSalary = baseSalaries[tier as keyof typeof baseSalaries];
      const percentDiff = Math.abs((salary - baseSalary) / baseSalary) * 100;
      if (percentDiff > 50) {
        const direction = salary > baseSalary ? 'above' : 'below';
        warnings.push(`${tier.charAt(0).toUpperCase() + tier.slice(1)} salary is ${Math.round(percentDiff)}% ${direction} industry average`);
      }
    });

    return warnings;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div className="relative bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">

            {/* Header */}
            <div className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Calculate Hiring Metrics</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Customize your hiring mix and location to calculate accurate metrics
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">

              {/* Quick Preset Selection */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 block mb-3">
                  Quick Presets - Select or customize below:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PRESET_PROFILES).map(([key, profile]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyPreset(key)}
                      className={`p-3 border rounded-lg text-left transition-all ${
                        selectedPreset === key
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900">{profile.name}</div>
                      <div className="text-xs text-gray-600 mt-1">{profile.description}</div>
                      <div className="text-xs text-gray-500 mt-2">
                        Entry: {profile.entry}% • Mid: {profile.mid}% • Senior: {profile.senior}% • Exec: {profile.executive}%
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Hiring Mix */}
              <div className="mb-6">
                <div className="mb-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">
                      Hiring Mix
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${total === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                        Total: {total}%
                      </span>
                      {total === 100 && (
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {/* Warning removed - sliders now auto-adjust to maintain 100% */}
                </div>
                <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                  {Object.entries({
                    entry: 'Entry Level (0-3 years)',
                    mid: 'Mid Level (3-7 years)',
                    senior: 'Senior Level (7+ years)',
                    executive: 'Executive/Leadership'
                  }).map(([key, label]) => (
                    <div key={key}>
                      <div className="flex justify-between mb-1">
                        <label className="text-sm text-gray-700">
                          {label}
                        </label>
                        <span className="text-sm font-medium text-gray-900">
                          {hiringMix[key as keyof HiringMix]}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={hiringMix[key as keyof HiringMix]}
                        onChange={(e) => handleMixChange(key as keyof HiringMix, parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Salary Customization (Optional) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    Salary Levels:
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowSalaryCustomization(!showSalaryCustomization)}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {showSalaryCustomization ? 'Hide' : 'Customize'} salaries
                  </button>
                </div>

                {showSalaryCustomization && (
                  <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                    {Object.entries({
                      entry: 'Entry Level',
                      mid: 'Mid Level',
                      senior: 'Senior Level',
                      executive: 'Executive'
                    }).map(([key, label]) => {
                      const tierKey = key as keyof typeof customSalaries;
                      const currentValue = customSalaries[tierKey] || baseSalaries[tierKey];
                      const isModified = isCustomized(tierKey);

                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-700">{label}</label>
                            {isModified && (
                              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                Customized
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">$</span>
                            <input
                              type="text"
                              value={formatWithCommas(currentValue)}
                              onChange={(e) => handleSalaryChange(tierKey, e.target.value)}
                              className={`flex-1 px-3 py-1.5 border rounded-md text-sm ${
                                isModified
                                  ? 'border-blue-300 bg-blue-50'
                                  : 'border-gray-300 bg-white'
                              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                              placeholder={formatWithCommas(baseSalaries[tierKey])}
                            />
                            {isModified && (
                              <button
                                type="button"
                                onClick={() => resetSalary(tierKey)}
                                className="text-gray-400 hover:text-gray-600 p-1"
                                title="Reset to default"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            Industry default: ${formatWithCommas(baseSalaries[tierKey])}
                          </div>
                        </div>
                      );
                    })}

                    {/* Validation Warnings */}
                    {getSalaryWarnings().length > 0 && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-amber-800 mb-1">Salary Validation</div>
                            <ul className="text-xs text-amber-700 space-y-1">
                              {getSalaryWarnings().map((warning, idx) => (
                                <li key={idx}>• {warning}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Location Adjustment */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Market Location:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(LOCATION_ADJUSTMENTS).map(([key, data]) => (
                    <button
                      key={key}
                      onClick={() => setLocation(key)}
                      className={`px-3 py-2 text-sm rounded-md border transition-all ${
                        location === key
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-300 hover:border-gray-400 text-gray-700'
                      }`}
                    >
                      {data.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-medium text-gray-900 mb-3">Calculated Values</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-700 block mb-1">Average Salary:</label>
                    <div className="text-2xl font-semibold text-gray-900">
                      ${formatWithCommas(avgSalary)}
                    </div>
                    <input
                      type="text"
                      value={manualSalary}
                      onChange={(e) => setManualSalary(e.target.value.replace(/[^0-9,]/g, ''))}
                      className="w-full mt-2 px-3 py-1 border border-gray-300 rounded-md text-sm"
                      placeholder="Override if needed"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700 block mb-1">Daily Vacancy Cost:</label>
                    <div className="text-2xl font-semibold text-gray-900">
                      ${formatWithCommas(dailyVacancy)}
                    </div>
                    <input
                      type="text"
                      value={manualVacancy}
                      onChange={(e) => setManualVacancy(e.target.value.replace(/[^0-9,]/g, ''))}
                      className="w-full mt-2 px-3 py-1 border border-gray-300 rounded-md text-sm"
                      placeholder="Override if needed"
                    />
                  </div>
                </div>

                {/* Calculation Details */}
                <div className="mt-4 pt-3 border-t border-blue-200">
                  <button
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <svg className={`w-4 h-4 transition-transform ${showBreakdown ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    Calculation Details
                  </button>

                  {showBreakdown && (
                    <div className="mt-3 space-y-3 text-sm">
                      <div>
                        <div className="font-medium text-gray-700 mb-1">Weighted Salary:</div>
                        {Object.entries(hiringMix).filter(([_, v]) => v > 0).map(([key, value]) => {
                          const tierKey = key as keyof typeof customSalaries;
                          const salary = customSalaries[tierKey] || baseSalaries[tierKey];
                          const contribution = Math.round((value / 100) * salary);

                          return (
                            <div key={key} className="text-gray-600 ml-3 flex justify-between">
                              <span>
                                {key.charAt(0).toUpperCase() + key.slice(1)}: {value}% × ${formatWithCommas(salary)}
                              </span>
                              <span className="font-medium">${formatWithCommas(contribution)}</span>
                            </div>
                          );
                        })}
                        <div className="ml-3 mt-2 pt-2 border-t flex justify-between font-medium">
                          <span>Location adjustment ({LOCATION_ADJUSTMENTS[location as keyof typeof LOCATION_ADJUSTMENTS].label})</span>
                          <span>${formatWithCommas(avgSalary)}</span>
                        </div>
                      </div>

                      <div>
                        <div className="font-medium text-gray-700 mb-1">Vacancy Impact:</div>
                        {Object.entries(hiringMix).filter(([_, v]) => v > 0).map(([key, value]) => (
                          <div key={key} className="text-gray-600 ml-3">
                            {key.charAt(0).toUpperCase() + key.slice(1)}: {value}% at {VACANCY_MULTIPLIERS[key as keyof typeof VACANCY_MULTIPLIERS]}x impact
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Attribution Footer */}
              <div className="text-xs text-gray-500 border-t pt-3 mt-4 space-y-1">
                <p>
                  <span className="font-medium">Salary ranges:</span> Based on BLS Occupational Employment Statistics
                </p>
                <p>
                  <span className="font-medium">Vacancy costs:</span> SHRM-validated multipliers (1.5-3.0x annual salary)
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                Use These Values
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};